import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { databases, DATABASE_ID, USERS_COLLECTION_ID, Query, ID, User } from '../config/appwrite';

interface UserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserRole?: 'admin' | 'user' | 'superadmin';
  currentUserId?: string;
}

type ImportPreview = {
  toCreate: Array<{ username: string; name: string; jamiaat: string; role: string }>;
  toUpdate: User[];
  toBlock: User[];
  fileName: string;
  errors: string[];
};

/* ─── tiny helpers ────────────────────────────────────────────── */
const Avatar: React.FC<{ name: string; username: string }> = ({ name, username }) => {
  const letter = name ? name.charAt(0).toUpperCase() : username.charAt(0).toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-[#0e4653] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 select-none">
      {letter}
    </div>
  );
};

const Badge: React.FC<{ label: string; color: 'green' | 'gray' | 'red' | 'amber' | 'teal' }> = ({
  label,
  color,
}) => {
  const map = {
    green: 'bg-emerald-100 text-emerald-700',
    gray: 'bg-gray-100 text-gray-600',
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
    teal: 'bg-[#0e4653]/10 text-[#0e4653]',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[color]}`}>
      {label}
    </span>
  );
};

/* ─── main component ──────────────────────────────────────────── */
const UserManagementModal: React.FC<UserManagementModalProps> = ({
  isOpen,
  onClose,
  currentUserRole,
  currentUserId,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [newUserNumber, setNewUserNumber] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserJamiaat, setNewUserJamiaat] = useState('');
  const [newUserTag, setNewUserTag] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [editName, setEditName] = useState('');
  const [editJamiaat, setEditJamiaat] = useState('');
  const [editTag, setEditTag] = useState('');
  const [editRole, setEditRole] = useState<'user' | 'admin' | 'superadmin'>('user');
  const [isSaving, setIsSaving] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBlockedOnly, setShowBlockedOnly] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [progressModal, setProgressModal] = useState<{
    title: string;
    subtitle: string;
    total: number;
    processed: number;
    done: boolean;
    accent: 'teal' | 'amber' | 'red';
    stats: Array<{ label: string; value: number; color: string }>;
  } | null>(null);
  const [importTag, setImportTag] = useState('');
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showImportSection, setShowImportSection] = useState(false);
  const [showCreateSection, setShowCreateSection] = useState(false);
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string>('all');
  const [isBlockingTag, setIsBlockingTag] = useState(false);
  const [isDeletingTag, setIsDeletingTag] = useState(false);
  const [showDeleteTagConfirm, setShowDeleteTagConfirm] = useState(false);
  const [isLoggingOutAll, setIsLoggingOutAll] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tagDropdownRef = useRef<HTMLDivElement>(null);
  const tagTriggerRef = useRef<HTMLButtonElement>(null);
  const [tagDropdownPos, setTagDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      setSearchQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setIsTagDropdownOpen(false);
        setTagSearchQuery('');
      }
    };
    if (isTagDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isTagDropdownOpen]);

  /* ── data ─────────────────────────────────────────────────────── */
  const fetchUsers = async () => {
    try {
      const response = await databases.listDocuments(DATABASE_ID, USERS_COLLECTION_ID, [
        Query.orderDesc('created_at'),
        Query.limit(10000),
      ]);
      const data = response.documents.map(doc => ({
        id: doc.$id,
        username: doc.username as string,
        role: doc.role as 'admin' | 'user' | 'superadmin',
        is_blocked: doc.is_blocked as boolean,
        is_logged_in: doc.is_logged_in as string,
        last_logout_tab: doc.last_logout_tab as string,
        name: doc.name as string,
        jamiaat: doc.jamiaat as string,
        password: doc.password as string,
        created_at: doc.created_at as string,
        last_active: doc.last_active as string | undefined,
        tag: doc.tag as string | undefined,
      }));
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  /* ── export ───────────────────────────────────────────────────── */
  const triggerDownload = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToCSV = () => {
    setProgressModal({
      title: 'Exporting CSV',
      subtitle: 'Preparing all users for download…',
      total: users.length,
      processed: 0,
      done: false,
      accent: 'amber',
      stats: [{ label: 'Users', value: users.length, color: 'text-amber-600' }],
    });
    try {
      const headers = ['Username', 'Name', 'Jamiaat', 'Role', 'Is Blocked', 'Is Logged In', 'Created At', 'Last Active', 'Tag'];
      const csvRows = users.map(user => [
        user.username, user.name || '', user.jamiaat || '', user.role,
        user.is_blocked ? 'Yes' : 'No',
        user.is_logged_in === 'true' ? 'Yes' : 'No',
        new Date(user.created_at).toLocaleString(),
        user.last_active ? new Date(user.last_active).toLocaleString() : 'Never',
        user.tag || '',
      ]);
      const csvContent = [headers.join(','), ...csvRows.map(r => r.map(c => {
        const s = String(c);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))].join('\n');
      triggerDownload(csvContent, `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      setProgressModal(p => p ? { ...p, processed: users.length, done: true, subtitle: 'Download started!' } : null);
    } catch {
      setProgressModal(p => p ? { ...p, done: true, subtitle: 'Export failed.' } : null);
    }
    setTimeout(() => setProgressModal(null), 2500);
  };

  const exportActiveUsersToCSV = () => {
    const activeUsers = users.filter(u => u.is_logged_in === 'true');
    if (!activeUsers.length) { toast.error('No active users to export'); return; }
    setProgressModal({
      title: 'Exporting Active Users',
      subtitle: 'Preparing active users for download…',
      total: activeUsers.length,
      processed: 0,
      done: false,
      accent: 'amber',
      stats: [{ label: 'Active Users', value: activeUsers.length, color: 'text-amber-600' }],
    });
    try {
      const csvContent = ['Username,Name', ...activeUsers.map(u => `${u.username},${u.name || ''}`)].join('\n');
      triggerDownload(csvContent, `active_users_export_${new Date().toISOString().split('T')[0]}.csv`);
      setProgressModal(p => p ? { ...p, processed: activeUsers.length, done: true, subtitle: 'Download started!' } : null);
    } catch {
      setProgressModal(p => p ? { ...p, done: true, subtitle: 'Export failed.' } : null);
    }
    setTimeout(() => setProgressModal(null), 2500);
  };

  /* ── csv parse ────────────────────────────────────────────────── */
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim()); current = '';
      } else current += char;
    }
    result.push(current.trim());
    return result;
  };

  /* ── import flow ──────────────────────────────────────────────── */
  const handleFileSelect = async (file: File) => {
    if (!importTag.trim()) { toast.error('Please enter an import tag first.'); return; }
    setIsParsingFile(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { toast.error('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      const missing = ['Username', 'Name', 'Jamiaat'].filter(c => !headers.includes(c));
      if (missing.length) {
        toast.error(`Missing columns: ${missing.join(', ')}`, { duration: 5000 });
        return;
      }
      const ui = headers.indexOf('Username'), ni = headers.indexOf('Name'),
        ji = headers.indexOf('Jamiaat'), ri = headers.indexOf('Role');
      const existingMap = new Map(users.map(u => [u.username, u]));
      const sheetUsernames = new Set<string>();
      const toCreate: ImportPreview['toCreate'] = [];
      const errors: string[] = [];

      for (let i = 1; i < lines.length; i++) {
        const row = parseCSVLine(lines[i]);
        if (row.length < 3) { errors.push(`Row ${i + 1}: insufficient columns`); continue; }
        const username = row[ui]?.trim();
        const name = row[ni]?.trim();
        const jamiaat = row[ji]?.trim();
        const roleRaw = ri >= 0 ? row[ri]?.trim().toLowerCase() : 'user';
        const role = roleRaw === 'admin' || roleRaw === 'superadmin' ? roleRaw : 'user';
        if (!username || !/^\d{8}$/.test(username)) {
          errors.push(`Row ${i + 1}: invalid username "${username}"`); continue;
        }
        sheetUsernames.add(username);
        if (!existingMap.has(username)) toCreate.push({ username, name: name || '', jamiaat: jamiaat || '', role });
      }

      // Users in sheet that already exist → update their tag
      const toUpdate = users.filter(u => sheetUsernames.has(u.username));

      // Only block users that share the SAME tag as the import AND are not in the sheet
      const toBlock = users.filter(
        u => u.role === 'user' && userHasTag(u, importTag.trim()) && !sheetUsernames.has(u.username)
      );

      setImportPreview({ toCreate, toUpdate, toBlock, fileName: file.name, errors });
    } catch (e: any) {
      toast.error('Error reading file: ' + (e?.message || 'Unknown error'));
    } finally {
      setIsParsingFile(false);
    }
  };

  const cancelImport = () => setImportPreview(null);

  const executeImport = async () => {
    if (!importPreview) return;
    setIsImporting(true);
    setImportPreview(null);
    const total = importPreview.toCreate.length + importPreview.toUpdate.length + importPreview.toBlock.length;
    let imported = 0, updated = 0, blocked = 0, errors = 0, processed = 0;

    const syncStats = () => setProgressModal(p => p ? {
      ...p, processed,
      stats: [
        { label: 'Created', value: imported, color: 'text-emerald-600' },
        { label: 'Tag Updated', value: updated, color: 'text-blue-600' },
        { label: 'Blocked', value: blocked, color: 'text-orange-600' },
        { label: 'Errors', value: errors, color: 'text-red-600' },
      ],
    } : null);

    setProgressModal({
      title: 'Importing Users',
      subtitle: `Tag: ${importTag.trim()}`,
      total,
      processed: 0,
      done: false,
      accent: 'teal',
      stats: [
        { label: 'Created', value: 0, color: 'text-emerald-600' },
        { label: 'Tag Updated', value: 0, color: 'text-blue-600' },
        { label: 'Blocked', value: 0, color: 'text-orange-600' },
        { label: 'Errors', value: 0, color: 'text-red-600' },
      ],
    });

    for (const row of importPreview.toCreate) {
      try {
        await databases.createDocument(DATABASE_ID, USERS_COLLECTION_ID, ID.unique(), {
          username: row.username, password: row.username, role: row.role,
          is_blocked: false, is_logged_in: 'false', name: row.name, jamiaat: row.jamiaat,
          last_logout_tab: '', created_at: new Date().toISOString(), tag: importTag.trim(),
        });
        imported++;
      } catch { errors++; }
      processed++;
      syncStats();
    }

    for (const user of importPreview.toUpdate) {
      try {
        await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, user.id, { tag: importTag.trim() });
        updated++;
      } catch { errors++; }
      processed++;
      syncStats();
    }

    for (const user of importPreview.toBlock) {
      try {
        await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, user.id, { is_blocked: true });
        blocked++;
      } catch { errors++; }
      processed++;
      syncStats();
    }

    await fetchUsers();
    setProgressModal(p => p ? { ...p, processed: total, done: true } : null);
    setTimeout(() => {
      if (errors > 0) toast.error(`Import done with ${errors} error(s). Created: ${imported}, Updated: ${updated}, Blocked: ${blocked}`, { duration: 6000 });
      else toast.success(`Import complete! Created: ${imported}, Updated: ${updated}, Blocked: ${blocked}`);
      setProgressModal(null);
      setIsImporting(false);
      setImportTag('');
      setShowImportSection(false);
    }, 2000);
  };

  /* ── block entire tag ─────────────────────────────────────────── */
  const handleBlockTag = async (tag: string) => {
    // Only block users whose sole tag is this one — skip multi-tag users
    const targets = users.filter(u => {
      if (!userHasTag(u, tag) || u.is_blocked) return false;
      const tags = u.tag!.split('|').map(t => t.trim()).filter(Boolean);
      return tags.length === 1;
    });
    if (targets.length === 0) { toast('No single-tag users to block (multi-tag users are skipped).'); return; }
    setIsBlockingTag(true);
    let done = 0, failed = 0;
    for (const user of targets) {
      try {
        await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, user.id, { is_blocked: true });
        done++;
      } catch { failed++; }
    }
    await fetchUsers();
    setIsBlockingTag(false);
    if (failed > 0) toast.error(`Blocked ${done} users. ${failed} failed.`);
    else toast.success(`Blocked ${done} users with tag "${tag}".`);
  };

  const handleDeleteTag = async (tag: string) => {
    // Strip the tag from users — do not delete the user
    const targets = users.filter(u => userHasTag(u, tag));
    if (targets.length === 0) { toast('No users found with this tag.'); return; }
    setIsDeletingTag(true);
    setShowDeleteTagConfirm(false);
    let done = 0, failed = 0;

    setProgressModal({
      title: `Removing Tag "${tag}"`,
      subtitle: `Stripping tag from ${targets.length} users…`,
      total: targets.length,
      processed: 0,
      done: false,
      accent: 'red',
      stats: [
        { label: 'Updated', value: 0, color: 'text-emerald-600' },
        { label: 'Failed', value: 0, color: 'text-red-600' },
      ],
    });

    for (const user of targets) {
      try {
        const remaining = user.tag!.split('|').map(t => t.trim()).filter(t => t !== tag);
        await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, user.id, {
          tag: remaining.length > 0 ? remaining.join('|') : null,
        });
        done++;
      } catch { failed++; }
      setProgressModal(p => p ? {
        ...p,
        processed: done + failed,
        stats: [
          { label: 'Updated', value: done, color: 'text-emerald-600' },
          { label: 'Failed', value: failed, color: 'text-red-600' },
        ],
      } : null);
    }

    await fetchUsers();
    setProgressModal(p => p ? { ...p, processed: targets.length, done: true } : null);
    setSelectedTag('all');
    setTimeout(() => {
      if (failed > 0) toast.error(`Removed tag from ${done} users. ${failed} failed.`);
      else toast.success(`Tag "${tag}" removed from ${done} users.`);
      setProgressModal(null);
      setIsDeletingTag(false);
    }, 2000);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  /* ── create user ──────────────────────────────────────────────── */
  const handleCreateUser = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    if (!/^\d{8}$/.test(newUserNumber)) { toast.error('Number must be 8 digits'); setIsLoading(false); return; }
    try {
      const existing = users.find(u => u.username === newUserNumber);
      if (existing) {
        // User exists — merge the new tag without duplicating
        const existingTags = existing.tag ? existing.tag.split('|').map(t => t.trim()) : [];
        const newTag = newUserTag.trim();
        if (newTag && !existingTags.includes(newTag)) {
          const mergedTag = [...existingTags, newTag].join('|');
          await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, existing.id, { tag: mergedTag });
          toast.success(`User already exists — added tag "${newTag}" to ${existing.name || existing.username}.`);
        } else {
          toast(`User ${existing.name || existing.username} already exists and already has this tag.`);
        }
      } else {
        await databases.createDocument(DATABASE_ID, USERS_COLLECTION_ID, ID.unique(), {
          username: newUserNumber, password: newUserNumber, role: 'user',
          is_blocked: false, is_logged_in: 'false', name: newUserName,
          jamiaat: newUserJamiaat, last_logout_tab: '', created_at: new Date().toISOString(),
          ...(newUserTag ? { tag: newUserTag } : {}),
        });
        toast.success(`User ${newUserName} created successfully!`);
      }
      setNewUserNumber(''); setNewUserName(''); setNewUserJamiaat(''); setNewUserTag('');
      fetchUsers();
    } catch (error: any) {
      toast.error(error?.message || 'Error creating user', { duration: 5000 });
    } finally {
      setIsLoading(false);
    }
  }, [newUserNumber, newUserName, newUserJamiaat, newUserTag, users]);

  const handleLogoutUser = async (userId: string) => {
    try {
      await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, userId, {
        is_logged_in: 'false',
        last_logout_tab: null,
      });
      fetchUsers();
      toast.success('User logged out successfully');
    } catch (error: any) {
      toast.error(error?.message || 'Error logging out user');
    }
  };

  const handleLogoutAll = async () => {
    const targets = users.filter(u => u.role === 'user' && u.is_logged_in === 'true');
    if (targets.length === 0) { toast('No active regular users to log out.'); return; }
    setIsLoggingOutAll(true);
    let done = 0, failed = 0;
    for (const user of targets) {
      try {
        await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, user.id, {
          is_logged_in: 'false',
          last_logout_tab: null,
        });
        done++;
      } catch { failed++; }
    }
    await fetchUsers();
    setIsLoggingOutAll(false);
    if (failed > 0) toast.error(`Logged out ${done} users. ${failed} failed.`);
    else toast.success(`Logged out ${done} users successfully`);
  };

  const handleDeleteAll = async () => {
    const targets = users.filter(u => u.id !== currentUserId);
    if (targets.length === 0) { toast('No users to delete.'); return; }
    setIsDeletingAll(true);
    setShowDeleteAllConfirm(false);
    let done = 0, failed = 0;

    setProgressModal({
      title: 'Deleting All Users',
      subtitle: 'Removing users from the database…',
      total: targets.length,
      processed: 0,
      done: false,
      accent: 'red',
      stats: [
        { label: 'Deleted', value: 0, color: 'text-emerald-600' },
        { label: 'Failed', value: 0, color: 'text-red-600' },
      ],
    });

    for (const user of targets) {
      try {
        await databases.deleteDocument(DATABASE_ID, USERS_COLLECTION_ID, user.id);
        done++;
      } catch { failed++; }
      setProgressModal(p => p ? {
        ...p,
        processed: done + failed,
        stats: [
          { label: 'Deleted', value: done, color: 'text-emerald-600' },
          { label: 'Failed', value: failed, color: 'text-red-600' },
        ],
      } : null);
    }

    await fetchUsers();
    setProgressModal(p => p ? { ...p, processed: targets.length, done: true } : null);
    setTimeout(() => {
      if (failed > 0) toast.error(`Deleted ${done} users. ${failed} failed.`);
      else toast.success(`Deleted ${done} users successfully`);
      setProgressModal(null);
      setIsDeletingAll(false);
    }, 2000);
  };

  const handleToggleBlock = async (userId: string, blocked: boolean) => {
    try {
      await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, userId, { is_blocked: !blocked });
      fetchUsers();
      toast.success(`User ${!blocked ? 'blocked' : 'unblocked'} successfully`);
    } catch (error: any) {
      toast.error(error?.message || 'Error updating user', { duration: 5000 });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await databases.deleteDocument(DATABASE_ID, USERS_COLLECTION_ID, userId);
      setUserToDelete(null);
      fetchUsers();
      toast.success('User deleted successfully');
    } catch (error: any) {
      toast.error(error?.message || 'Error deleting user', { duration: 5000 });
    }
  };

  const openEdit = (user: User) => {
    setUserToEdit(user);
    setEditName(user.name || '');
    setEditJamiaat(user.jamiaat || '');
    setEditTag(user.tag || '');
    setEditRole(user.role);
  };

  const handleSaveEdit = async () => {
    if (!userToEdit) return;
    setIsSaving(true);
    try {
      await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, userToEdit.id, {
        name: editName,
        jamiaat: editJamiaat,
        tag: editTag || null,
        role: editRole,
      });
      await fetchUsers();
      setUserToEdit(null);
      toast.success('User updated successfully');
    } catch (error: any) {
      toast.error(error?.message || 'Error updating user');
    } finally {
      setIsSaving(false);
    }
  };

  const userHasTag = (u: User, tag: string) =>
    !!u.tag && u.tag.split('|').map(t => t.trim()).includes(tag);

  const allTags = Array.from(new Set(
    users.flatMap(u => u.tag ? u.tag.split('|').map(t => t.trim()).filter(Boolean) : [])
  )).sort();

  const filteredUsers = (isActive: boolean) =>
    users
      .filter(u => {
        if (showBlockedOnly) return u.is_blocked && (isActive ? u.is_logged_in === 'true' : u.is_logged_in !== 'true');
        return isActive ? u.is_logged_in === 'true' : u.is_logged_in !== 'true';
      })
      .filter(u =>
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.name && u.name.toLowerCase().includes(searchQuery.toLowerCase()))
      )
      .filter(u => selectedTag === 'all' || userHasTag(u, selectedTag))
      .sort((a, b) => parseInt(a.username) - parseInt(b.username));

  const handleClose = () => { setSearchQuery(''); setSelectedTag('all'); onClose(); };

  if (!isOpen) return null;

  const isSuperAdmin = currentUserRole === 'superadmin';

  /* ─── render ──────────────────────────────────────────────────── */
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-3 sm:p-6 overflow-y-auto">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl my-4 overflow-hidden">

        {/* ── Modal Header ─────────────────────────────────── */}
        <div className="bg-[#0e4653] px-5 sm:px-7 pt-5 pb-0">
          {/* Row 1: title + create + close */}
          <div className="flex items-center justify-between gap-3 pb-4">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-white">User Management</h3>
              <p className="text-[#7fb8c8] text-xs sm:text-sm mt-0.5">{users.length} total users</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Create User toggle — admin & superadmin */}
              {(currentUserRole === 'admin' || currentUserRole === 'superadmin') && (
                <button
                  onClick={() => setShowCreateSection(p => !p)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    showCreateSection
                      ? 'bg-[#B98027] text-white border-[#B98027]'
                      : 'bg-white/10 text-white border-white/30 hover:bg-white/20'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Create User</span>
                  <svg className={`w-3.5 h-3.5 transition-transform ${showCreateSection ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
              {/* Close */}
              <button
                onClick={handleClose}
                className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Row 2: superadmin tools bar */}
          {isSuperAdmin && (
            <div className="border-t border-white/10 py-3 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 mr-1 select-none">
                Superadmin
              </span>

              {/* Import Bulk */}
              <button
                onClick={() => setShowImportSection(p => !p)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                  showImportSection
                    ? 'bg-white text-[#0e4653] border-white'
                    : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Import Bulk
                <svg className={`w-3 h-3 transition-transform ${showImportSection ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Export CSV */}
              <button
                onClick={exportToCSV}
                disabled={!users.length}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#B98027]/60 bg-[#B98027]/20 text-[#f0c060] hover:bg-[#B98027]/30 disabled:opacity-40 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </button>

              {/* Delete All */}
              <button
                onClick={() => setShowDeleteAllConfirm(true)}
                disabled={isDeletingAll || !users.filter(u => u.id !== currentUserId).length}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-500/40 bg-red-500/15 text-red-300 hover:bg-red-500/25 disabled:opacity-40 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {isDeletingAll ? 'Deleting…' : 'Delete All Users'}
              </button>
            </div>
          )}
        </div>

        {/* ── Body ─────────────────────────────────────────── */}
        <div className="px-5 sm:px-7 py-6 space-y-6 overflow-y-auto max-h-[calc(90vh-80px)]">

          {/* ── Import Section (collapsible) ─────────────── */}
          {isSuperAdmin && showImportSection && (
            <div className="border border-[#0e4653]/20 rounded-xl overflow-hidden">
              <div className="bg-[#0e4653]/5 px-5 py-3 border-b border-[#0e4653]/10">
                <h4 className="text-sm font-semibold text-[#0e4653]">Import Users from CSV</h4>
                <p className="text-xs text-gray-500 mt-0.5">Tag new users, sync DB — unmatched users will be blocked</p>
              </div>
              <div className="p-5 space-y-4">
                {/* Tag input */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Import Tag <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={importTag}
                    onChange={e => setImportTag(e.target.value)}
                    placeholder="e.g. June2026, Miqaat1447..."
                    disabled={isImporting || isParsingFile}
                    className="w-full sm:w-72 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0e4653]/30 focus:border-[#0e4653] disabled:bg-gray-50 transition"
                  />
                </div>

                {/* Drop zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => !isImporting && !isParsingFile && fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer select-none ${
                    isDragging
                      ? 'border-[#0e4653] bg-[#0e4653]/5 scale-[1.01]'
                      : 'border-gray-300 hover:border-[#0e4653]/50 hover:bg-gray-50/50'
                  } ${isImporting || isParsingFile ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleFileSelect(f); }}
                    className="hidden"
                    disabled={isImporting || isParsingFile}
                  />
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-[#0e4653]/10 flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#0e4653]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    {isParsingFile ? (
                      <p className="text-sm text-[#0e4653] font-medium animate-pulse">Parsing file…</p>
                    ) : (
                      <>
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-[#0e4653]">Click to upload</span> or drag &amp; drop
                        </p>
                        <p className="text-xs text-gray-400">CSV files only · Columns: Username, Name, Jamiaat</p>
                      </>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* ── Create New User (collapsible) ────────────── */}
          {showCreateSection && <div className="border border-[#B98027]/25 rounded-xl overflow-hidden">
            <div className="bg-[#B98027]/8 px-5 py-3 border-b border-[#B98027]/15">
              <h4 className="text-sm font-semibold text-[#0e4653]">Create New User</h4>
              <p className="text-xs text-gray-500 mt-0.5">Username is used as the default password</p>
            </div>
            <div className="p-5">
              <form onSubmit={handleCreateUser}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                      8-Digit Number
                    </label>
                    <input
                      type="text"
                      value={newUserNumber}
                      onChange={e => setNewUserNumber(e.target.value)}
                      pattern="\d{8}"
                      required
                      disabled={isLoading}
                      placeholder="12345678"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B98027]/30 focus:border-[#B98027] disabled:bg-gray-50 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Name</label>
                    <input
                      type="text"
                      value={newUserName}
                      onChange={e => setNewUserName(e.target.value)}
                      required
                      disabled={isLoading}
                      placeholder="Full name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B98027]/30 focus:border-[#B98027] disabled:bg-gray-50 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Jamiaat</label>
                    <input
                      type="text"
                      value={newUserJamiaat}
                      onChange={e => setNewUserJamiaat(e.target.value)}
                      required
                      disabled={isLoading}
                      placeholder="Jamiaat name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B98027]/30 focus:border-[#B98027] disabled:bg-gray-50 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                      Tag <span className="text-red-500">*</span>
                    </label>
                    <div className="relative" ref={tagDropdownRef}>
                      {/* Trigger */}
                      <button
                        ref={tagTriggerRef}
                        type="button"
                        disabled={isLoading}
                        onClick={() => {
                          if (!isTagDropdownOpen && tagTriggerRef.current) {
                            const r = tagTriggerRef.current.getBoundingClientRect();
                            setTagDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
                          }
                          setIsTagDropdownOpen(p => !p);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-sm transition disabled:bg-gray-50 focus:outline-none ${
                          newUserTag
                            ? 'border-amber-300 bg-amber-50 text-amber-800 font-medium'
                            : 'border-gray-300 bg-white text-gray-400'
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          {newUserTag ? (
                            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">{newUserTag}</span>
                          ) : 'Select or create a tag…'}
                        </span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isTagDropdownOpen ? 'rotate-180' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Hidden input for form required validation */}
                      <input type="text" required value={newUserTag} onChange={() => {}} className="sr-only" tabIndex={-1} />

                      {/* Dropdown panel — fixed to escape overflow:hidden parents */}
                      {isTagDropdownOpen && (
                        <div
                          className="fixed z-[200] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
                          style={{ top: tagDropdownPos.top, left: tagDropdownPos.left, width: tagDropdownPos.width }}
                        >
                          {/* Search */}
                          <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0" />
                              </svg>
                              <input
                                type="text"
                                autoFocus
                                value={tagSearchQuery}
                                onChange={e => setTagSearchQuery(e.target.value)}
                                placeholder="Search tags…"
                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B98027]/30 focus:border-[#B98027]"
                              />
                            </div>
                          </div>

                          {/* Options list */}
                          <div className="max-h-44 overflow-y-auto">
                            {(() => {
                              const filtered = allTags.filter(t =>
                                t.toLowerCase().includes(tagSearchQuery.toLowerCase())
                              );
                              const exactMatch = allTags.some(
                                t => t.toLowerCase() === tagSearchQuery.toLowerCase()
                              );
                              return (
                                <>
                                  {filtered.length === 0 && !tagSearchQuery && (
                                    <p className="px-3 py-4 text-xs text-gray-400 text-center">No existing tags</p>
                                  )}
                                  {filtered.map(tag => (
                                    <button
                                      key={tag}
                                      type="button"
                                      onClick={() => {
                                        setNewUserTag(tag);
                                        setIsTagDropdownOpen(false);
                                        setTagSearchQuery('');
                                      }}
                                      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-amber-50 transition-colors ${
                                        newUserTag === tag ? 'bg-amber-50 text-amber-800 font-semibold' : 'text-gray-700'
                                      }`}
                                    >
                                      <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                                      {tag}
                                    </button>
                                  ))}
                                  {tagSearchQuery && !exactMatch && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setNewUserTag(tagSearchQuery.trim());
                                        setIsTagDropdownOpen(false);
                                        setTagSearchQuery('');
                                      }}
                                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-[#0e4653] font-semibold hover:bg-[#0e4653]/5 border-t border-gray-100 transition-colors"
                                    >
                                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                      </svg>
                                      Create "{tagSearchQuery.trim()}"
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-5 py-2 bg-[#B98027] text-white text-sm font-semibold rounded-lg hover:bg-[#a06f20] focus:outline-none focus:ring-2 focus:ring-[#B98027]/40 disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? 'Creating…' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>}

          {/* ── User List ─────────────────────────────────── */}
          <div>
            {/* Search + filter bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              {/* Search */}
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by username or name…"
                  className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0e4653]/20 focus:border-[#0e4653] transition"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Tag filter */}
              <div className="relative shrink-0">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <select
                  value={selectedTag}
                  onChange={e => setSelectedTag(e.target.value)}
                  className={`pl-8 pr-7 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0e4653]/20 focus:border-[#0e4653] transition appearance-none cursor-pointer ${
                    selectedTag !== 'all'
                      ? 'border-amber-300 bg-amber-50 text-amber-800 font-medium'
                      : 'border-gray-300 bg-white text-gray-600'
                  }`}
                >
                  <option value="all">All Tags ({users.length})</option>
                  {allTags.map(tag => (
                    <option key={tag} value={tag}>
                      {tag} ({users.filter(u => userHasTag(u, tag)).length})
                    </option>
                  ))}
                </select>
                <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Blocked toggle */}
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none shrink-0">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={showBlockedOnly}
                    onChange={e => setShowBlockedOnly(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-checked:bg-[#0e4653] rounded-full transition-colors" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform" />
                </div>
                Blocked only
              </label>
            </div>

            {/* ── Tag actions row ── */}
            {selectedTag !== 'all' && (
              <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="text-sm font-semibold text-amber-800 truncate">{selectedTag}</span>
                  <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                    {users.filter(u => userHasTag(u, selectedTag)).length} users
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleBlockTag(selectedTag)}
                    disabled={isBlockingTag || isDeletingTag}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-orange-300 text-orange-700 text-xs font-semibold rounded-lg hover:bg-orange-50 disabled:opacity-50 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    {isBlockingTag ? 'Blocking…' : 'Block Tag'}
                  </button>
                  {isSuperAdmin && (
                    <button
                      onClick={() => setShowDeleteTagConfirm(true)}
                      disabled={isBlockingTag || isDeletingTag}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-300 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Tag
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Accordions */}
            <div className="space-y-3">
              {/* ── Active Users ── */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50/80">
                  <button
                    onClick={() => setActiveAccordion(activeAccordion === 'active' ? null : 'active')}
                    className="flex items-center gap-2 flex-1 min-w-0"
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span className="text-sm font-semibold text-[#0e4653]">Active Users</span>
                    <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                      {filteredUsers(true).length}
                    </span>
                  </button>
                  <div className="flex items-center gap-2">
                    {/* Logout All button — hidden for now */}
                    {/* Refresh button */}
                    <button
                      onClick={async e => {
                        e.stopPropagation();
                        setIsRefreshing(true);
                        await fetchUsers();
                        setIsRefreshing(false);
                      }}
                      disabled={isRefreshing}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-[#0e4653] hover:bg-[#0e4653]/5 transition-colors disabled:opacity-40"
                      title="Refresh users"
                    >
                      <svg
                        className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    {isSuperAdmin && (
                      <button
                        onClick={e => { e.stopPropagation(); exportActiveUsersToCSV(); }}
                        disabled={!filteredUsers(true).length}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-[#B98027] text-white text-xs font-medium rounded-lg hover:bg-[#a06f20] disabled:opacity-40 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export
                      </button>
                    )}
                    <button
                      onClick={() => setActiveAccordion(activeAccordion === 'active' ? null : 'active')}
                      className="p-1 rounded text-gray-400 hover:text-gray-600"
                    >
                      <svg className={`w-4 h-4 transition-transform ${activeAccordion === 'active' ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {activeAccordion === 'active' && (
                  <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                    {filteredUsers(true).length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">
                        {searchQuery ? 'No matching active users' : 'No active users'}
                      </div>
                    ) : (
                      filteredUsers(true).map(user => (
                        <UserRow
                          key={user.id}
                          user={user}
                          isActive
                          onToggleBlock={() => handleToggleBlock(user.id, user.is_blocked)}
                          onDelete={() => setUserToDelete(user.id)}
                          onLogout={() => handleLogoutUser(user.id)}
                          onEdit={() => openEdit(user)}
                          currentUserRole={currentUserRole}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* ── Inactive Users ── */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setActiveAccordion(activeAccordion === 'inactive' ? null : 'inactive')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50/80 hover:bg-gray-100/60 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-gray-400 flex-shrink-0" />
                    <span className="text-sm font-semibold text-[#0e4653]">Inactive Users</span>
                    <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full font-semibold">
                      {filteredUsers(false).length}
                    </span>
                  </div>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${activeAccordion === 'inactive' ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {activeAccordion === 'inactive' && (
                  <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                    {filteredUsers(false).length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">
                        {searchQuery ? 'No matching inactive users' : 'No inactive users'}
                      </div>
                    ) : (
                      filteredUsers(false).map(user => (
                        <UserRow
                          key={user.id}
                          user={user}
                          isActive={false}
                          onToggleBlock={() => handleToggleBlock(user.id, user.is_blocked)}
                          onDelete={() => setUserToDelete(user.id)}
                          onEdit={() => openEdit(user)}
                          currentUserRole={currentUserRole}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end pt-2">
            <button
              onClick={handleClose}
              className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* ── Import Preview Modal ──────────────────────────── */}
      {importPreview && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-[#0e4653] px-6 py-4">
              <h3 className="text-base font-semibold text-white">Confirm Import</h3>
              <p className="text-[#7fb8c8] text-xs mt-0.5">{importPreview.fileName} · Tag: <span className="font-semibold text-white">{importTag}</span></p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Will Create', value: importPreview.toCreate.length, color: 'bg-emerald-50 text-emerald-700', border: 'border-emerald-200' },
                  { label: 'Tag Updated', value: importPreview.toUpdate.length, color: 'bg-blue-50 text-blue-700', border: 'border-blue-200' },
                  { label: 'Will Block', value: importPreview.toBlock.length, color: 'bg-red-50 text-red-700', border: 'border-red-200' },
                ].map(s => (
                  <div key={s.label} className={`${s.color} border ${s.border} rounded-xl px-3 py-3 text-center`}>
                    <p className="text-2xl font-bold">{s.value}</p>
                    <p className="text-xs font-medium mt-0.5 opacity-80">{s.label}</p>
                  </div>
                ))}
              </div>

              {importPreview.toBlock.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Users that will be blocked</p>
                  <div className="max-h-36 overflow-y-auto bg-red-50 border border-red-100 rounded-lg p-3 space-y-1">
                    {importPreview.toBlock.slice(0, 30).map(u => (
                      <div key={u.id} className="flex items-center gap-2 text-xs text-red-700">
                        <span className="font-mono font-medium">{u.username}</span>
                        <span className="text-red-400">·</span>
                        <span>{u.name}</span>
                        {u.tag && <span className="ml-auto text-red-400 opacity-70">{u.tag}</span>}
                      </div>
                    ))}
                    {importPreview.toBlock.length > 30 && (
                      <p className="text-xs text-red-400 italic pt-1">+{importPreview.toBlock.length - 30} more</p>
                    )}
                  </div>
                </div>
              )}

              {importPreview.errors.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1.5">Parse errors (will be skipped)</p>
                  <div className="max-h-20 overflow-y-auto bg-amber-50 border border-amber-100 rounded-lg p-3 space-y-0.5">
                    {importPreview.errors.slice(0, 8).map((e, i) => (
                      <p key={i} className="text-xs text-amber-700">{e}</p>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  onClick={cancelImport}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeImport}
                  className="flex-1 px-4 py-2.5 bg-[#0e4653] text-white rounded-lg text-sm font-semibold hover:bg-[#0c3a45] transition-colors"
                >
                  Confirm Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit User Modal ──────────────────────────────── */}
      {userToEdit && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60 p-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-[#0e4653] px-6 py-4">
              <h3 className="text-base font-semibold text-white">Edit User</h3>
              <p className="text-[#7fb8c8] text-xs mt-0.5 font-mono">{userToEdit.username}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0e4653]/30 focus:border-[#0e4653] disabled:bg-gray-50 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Jamiaat</label>
                <input
                  type="text"
                  value={editJamiaat}
                  onChange={e => setEditJamiaat(e.target.value)}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0e4653]/30 focus:border-[#0e4653] disabled:bg-gray-50 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Tag</label>
                <select
                  value={editTag}
                  onChange={e => setEditTag(e.target.value)}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0e4653]/30 focus:border-[#0e4653] disabled:bg-gray-50 transition appearance-none bg-white"
                >
                  <option value="">— No tag —</option>
                  {allTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
              {currentUserRole === 'superadmin' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Role</label>
                  <select
                    value={editRole}
                    onChange={e => setEditRole(e.target.value as 'user' | 'admin' | 'superadmin')}
                    disabled={isSaving}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0e4653]/30 focus:border-[#0e4653] disabled:bg-gray-50 transition appearance-none bg-white"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Superadmin</option>
                  </select>
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setUserToEdit(null)}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2.5 bg-[#0e4653] text-white rounded-lg text-sm font-semibold hover:bg-[#0c3a45] disabled:opacity-50 transition-colors"
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Progress Popup ───────────────────────────────────── */}
      {progressModal && (() => {
        const pct = progressModal.total > 0
          ? Math.round((progressModal.processed / progressModal.total) * 100)
          : (progressModal.done ? 100 : 0);
        const accentBar = progressModal.accent === 'red'
          ? 'bg-red-500'
          : progressModal.accent === 'amber'
          ? 'bg-[#B98027]'
          : 'bg-[#0e4653]';
        const accentHeader = progressModal.accent === 'red'
          ? 'bg-red-600'
          : progressModal.accent === 'amber'
          ? 'bg-[#B98027]'
          : 'bg-[#0e4653]';
        return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* header */}
              <div className={`${accentHeader} px-6 py-4 flex items-center gap-3`}>
                {progressModal.done ? (
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <svg className="w-5 h-5 text-white/70 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                <div>
                  <p className="text-sm font-semibold text-white">{progressModal.title}</p>
                  <p className="text-xs text-white/70 mt-0.5">{progressModal.subtitle}</p>
                </div>
              </div>
              {/* progress bar */}
              <div className="px-6 pt-5 pb-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-500">
                    {progressModal.done ? 'Complete' : `${progressModal.processed} / ${progressModal.total}`}
                  </span>
                  <span className="text-xs font-semibold text-gray-700">{pct}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`${accentBar} h-2.5 rounded-full transition-all duration-300`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              {/* stats */}
              {progressModal.stats.length > 0 && (
                <div className={`px-6 pb-5 pt-3 grid gap-2 ${progressModal.stats.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
                  {progressModal.stats.map(s => (
                    <div key={s.label} className="bg-gray-50 rounded-xl px-3 py-2.5 text-center">
                      <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Delete Tag Confirmation Modal ────────────────────── */}
      {showDeleteTagConfirm && selectedTag !== 'all' && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60 p-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Remove Tag "{selectedTag}"</h3>
              <p className="text-sm text-gray-500">
                This will remove the tag from{' '}
                <span className="font-semibold text-red-600">
                  {users.filter(u => userHasTag(u, selectedTag)).length} users
                </span>.
                {' '}Users with multiple tags will keep their other tags. Users are not deleted.
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowDeleteTagConfirm(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteTag(selectedTag)}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                >
                  Remove Tag
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete All Confirmation Modal ────────────────────── */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60 p-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Delete All Users</h3>
              <p className="text-sm text-gray-500">
                This will permanently delete <span className="font-semibold text-red-600">{users.filter(u => u.id !== currentUserId).length} users</span>. Your account will not be deleted. This cannot be undone.
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowDeleteAllConfirm(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAll}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                >
                  Delete All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────── */}
      {userToDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60 p-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Delete User</h3>
              <p className="text-sm text-gray-500">This action cannot be undone. The user will be permanently removed.</p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setUserToDelete(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(userToDelete)}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── UserRow sub-component ───────────────────────────────────── */
const UserRow: React.FC<{
  user: User;
  isActive: boolean;
  onToggleBlock: () => void;
  onDelete: () => void;
  onLogout?: () => void;
  onEdit: () => void;
  currentUserRole?: 'admin' | 'user' | 'superadmin';
}> = ({ user, isActive, onToggleBlock, onDelete, onLogout, onEdit, currentUserRole }) => (
  <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/70 transition-colors">
    <div className="w-9 h-9 rounded-full bg-[#0e4653] flex items-center justify-center text-white text-sm font-bold flex-shrink-0 select-none">
      {(user.name || user.username).charAt(0).toUpperCase()}
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
        <span className="text-sm font-semibold text-gray-900 font-mono">{user.username}</span>
        {isActive
          ? <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Active</span>
          : <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Inactive</span>
        }
        {user.is_blocked && (
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Blocked</span>
        )}
        {user.role !== 'user' && (
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#0e4653]/10 text-[#0e4653] font-medium capitalize">{user.role}</span>
        )}
      </div>
      <p className="text-xs text-gray-500 truncate">
        {user.name || '—'} <span className="text-gray-300 mx-1">·</span> {user.jamiaat || '—'}
      </p>
      {user.tag && (
        <div className="flex flex-wrap gap-1 mt-1">
          {user.tag.split('|').map(t => t.trim()).filter(Boolean).map(t => (
            <span key={t} className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">{t}</span>
          ))}
        </div>
      )}
    </div>

    <div className="flex items-center gap-1.5 flex-shrink-0">
      {/* Individual logout button — hidden for now */}
      {(currentUserRole === 'admin' || currentUserRole === 'superadmin') && (
        <button
          onClick={onEdit}
          className="p-1.5 rounded-lg text-gray-400 hover:text-[#0e4653] hover:bg-[#0e4653]/5 transition-colors"
          title="Edit user"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      )}
      <button
        onClick={onToggleBlock}
        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
          user.is_blocked
            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            : 'bg-red-50 text-red-600 hover:bg-red-100'
        }`}
      >
        {user.is_blocked ? 'Unblock' : 'Block'}
      </button>
      <button
        onClick={onDelete}
        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
        title="Delete user"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>
  </div>
);

export default UserManagementModal;
